import firebase from 'firebase/app'
import 'firebase/messaging'
import { HTTP_API_V2 } from '../http-common'

// Public Web Push (VAPID) key from Firebase Console -> Project settings ->
// Cloud Messaging -> Web Push certificates. Injected at build time.
const VAPID_KEY = process.env.FCM_VAPID_KEY || ''
const SERVICE_WORKER_PATH = '/firebase-messaging-sw.js'

let registrationInProgress = false
let foregroundHandlerInstalled = false

export function isWebPushSupported () {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    firebase.messaging.isSupported()
  )
}

export function getNotificationPermission () {
  if (typeof Notification === 'undefined') {
    return 'unsupported'
  }
  return Notification.permission
}

function sendTokenToBackend (token) {
  if (!token) {
    return Promise.resolve()
  }
  // The `client: 'platform'` tag distinguishes this web token from the mobile
  // app token so a single user can keep both registered.
  return HTTP_API_V2.post('/fcm/tokens', { token, client: 'platform' })
    .catch((err) => {
      // Non-fatal: registration is retried on the next app load.
      console.error('Failed to register web push token', err)
    })
}

// Shows a native notification for FCM messages that arrive while a Platform tab
// is focused. FCM does not auto-display foreground messages, so we render the
// new alarm here using the SAME shape the backend sends and the service worker
// uses for background messages: the notification `title`/`body` from the
// payload (e.g. "High temperature - SMAAI5 1234" / barn name), the app icon and
// the original `data`. Displaying through the service worker registration means
// clicks are handled by the worker's existing notificationclick handler, so a
// foreground notification behaves exactly like a background one.
function setupForegroundMessageHandler (messaging) {
  if (foregroundHandlerInstalled) {
    return
  }
  foregroundHandlerInstalled = true

  messaging.onMessage((payload) => {
    if (getNotificationPermission() !== 'granted') {
      return
    }
    const notification = (payload && payload.notification) || {}
    const data = (payload && payload.data) || {}
    const title = notification.title || data.title || 'Inobram'
    const options = {
      body: notification.body || data.body || '',
      icon: '/static/img/icons/android-chrome-192x192.png',
      data: data
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((registration) => registration.showNotification(title, options))
        .catch((err) => console.error('Failed to show foreground notification', err))
    }
  })
}

// Registers the service worker, requests notification permission (showing the
// browser prompt) and registers the resulting FCM token with the backend.
// Safe to call on every login: it is a no-op when unsupported, already denied,
// or not configured.
export async function registerWebPush () {
  if (registrationInProgress) {
    return
  }
  if (!isWebPushSupported()) {
    return
  }
  if (!VAPID_KEY) {
    console.warn('Web push disabled: FCM_VAPID_KEY is not configured')
    return
  }
  // Don't nag a user who already denied — they must re-enable it in the
  // browser's site settings.
  if (Notification.permission === 'denied') {
    return
  }

  registrationInProgress = true
  try {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH)
    const messaging = firebase.messaging()
    messaging.useServiceWorker(registration)
    messaging.usePublicVapidKey(VAPID_KEY)
    setupForegroundMessageHandler(messaging)

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return
    }

    const token = await messaging.getToken()
    await sendTokenToBackend(token)

    messaging.onTokenRefresh(async () => {
      try {
        const refreshedToken = await messaging.getToken()
        await sendTokenToBackend(refreshedToken)
      } catch (err) {
        console.error('Failed to refresh web push token', err)
      }
    })
  } catch (err) {
    console.error('Web push registration failed', err)
  } finally {
    registrationInProgress = false
  }
}



// WEBPACK FOOTER //
// ./src/utils/web-push.js